export { }

import { defineStore } from 'pinia'
import { auth, db } from '../firebase'
import {
  signInAnonymously,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  linkWithCredential,
  EmailAuthProvider,
  deleteUser,
  reauthenticateWithCredential,
  signOut
} from 'firebase/auth'
import { onSnapshot, doc } from 'firebase/firestore'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import { getUserProfile } from '../services/familyService'
import { triggerFamilyUpdate } from '../services/pushNotificationService'
import type { FamilyMember, PendingMember } from '../types'

// 模組級別的 listener 清理函式（避免放入 Pinia state 導致序列化問題）
let profileUnsubscribe: (() => void) | null = null

// 判斷平台
const isCapacitor = typeof window !== 'undefined' && (window as any).Capacitor !== undefined
const platform = isCapacitor ? (window as any).Capacitor?.getPlatform?.() : 'web'

// 只在 iOS 上使用原生 API，Android 直接用 Web SDK（原生 API 有超時問題）
const useNativeAuth = isCapacitor && platform === 'ios'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    uid: null as string | null,
    ready: false,
    isAnonymous: true,
    hasShownBackupReminder: false,
    // 家庭共享狀態
    familyCode: null as string | null,
    familyName: null as string | null,       // 自己設定的家庭名稱（owner）
    ownerFamilyName: null as string | null,  // 加入的家庭名稱（guest）
    sharedWith: {} as Record<string, FamilyMember>,
    pendingMembers: {} as Record<string, PendingMember>, // 待 owner 審核的申請
    linkedTo: null as { uid: string; familyCode: string } | null,
    pendingWith: null as { uid: string; familyCode: string } | null, // guest 申請中
  }),

  getters: {
    /**
     * 資料路徑使用的 uid：
     * - guest（有 linkedTo）→ 使用 owner 的 uid
     * - owner / 一般用戶 → 使用自己的 uid
     */
    dataOwnerUid: (state): string | null => state.linkedTo?.uid ?? state.uid,
  },

  actions: {
    async init() {
      // iOS: 使用原生 Firebase Authentication 插件
      if (useNativeAuth) {
        try {
          // 設定超時（5秒）
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('Auth 初始化超時')), 5000)
          })

          // 檢查當前用戶（帶超時）
          const currentUserResult = await Promise.race([
            FirebaseAuthentication.getCurrentUser(),
            timeoutPromise
          ])

          if (currentUserResult.user) {
            // 驗證 token 以確認用戶仍然有效（檢測已刪除的用戶）
            try {
              await Promise.race([
                FirebaseAuthentication.getIdToken({ forceRefresh: true }),
                timeoutPromise
              ])

              this.uid = currentUserResult.user.uid
              this.isAnonymous = currentUserResult.user.isAnonymous || false
              this.ready = true
              // 啟動即時監聽（同時完成初始載入）
              this.startProfileListener()
              return
            } catch (tokenError: any) {
              // Token 驗證失敗，清除這個無效的 session
              await FirebaseAuthentication.signOut()

              // 重置狀態，等待用戶選擇
              this.uid = null
              this.ready = true
              this.isAnonymous = true
              return
            }
          }

          // 沒有用戶，不自動建立匿名帳號，等待用戶選擇
          this.uid = null
          this.ready = true
          this.isAnonymous = true
          return
        } catch (error: any) {
          console.error('Capacitor 原生登入失敗:', error?.message)
          // 超時或其他錯誤，仍然讓 app 可用
          this.uid = null
          this.ready = true
          this.isAnonymous = true
          return
        }
      }

      // Android/Web: 使用 onAuthStateChanged 方式
      return new Promise<void>((resolve, reject) => {
        // 設定 10 秒超時
        const timeout = setTimeout(() => {
          console.error('Auth 初始化超時（10秒）')
          reject(new Error('Auth 初始化超時'))
        }, 10000)

        try {
          let hasResolved = false

          const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (hasResolved) return // 防止多次觸發

            try {
              if (user) {
                // 驗證 token 以確認用戶仍然有效（檢測已刪除的用戶）
                try {
                  await user.getIdToken(true) // forceRefresh = true

                  this.uid = user.uid
                  this.isAnonymous = user.isAnonymous
                  this.ready = true
                  hasResolved = true
                  clearTimeout(timeout)
                  unsubscribe()
                  // 啟動即時監聽（同時完成初始載入）
                  this.startProfileListener()
                  resolve()
                } catch (tokenError: any) {
                  // Token 驗證失敗，清除這個無效的 session
                  await signOut(auth)

                  // 重置狀態，等待用戶選擇
                  this.uid = null
                  this.ready = true
                  this.isAnonymous = true
                  hasResolved = true
                  clearTimeout(timeout)
                  unsubscribe()
                  resolve()
                }
              } else {
                // 沒有用戶，不自動建立匿名帳號，等待用戶選擇
                this.uid = null
                this.ready = true
                this.isAnonymous = true
                hasResolved = true
                clearTimeout(timeout)
                unsubscribe()
                resolve()
              }
            } catch (error: any) {
              console.error('Auth 狀態處理失敗:', error)
              if (!hasResolved) {
                hasResolved = true
                clearTimeout(timeout)
                unsubscribe()
                reject(error)
              }
            }
          }, (error: any) => {
            console.error('onAuthStateChanged 回調錯誤:', error)
            if (!hasResolved) {
              hasResolved = true
              clearTimeout(timeout)
              reject(error)
            }
          })
        } catch (error: any) {
          console.error('Auth init 外層捕獲錯誤:', error)
          clearTimeout(timeout)
          reject(error)
        }
      })
    },

    async createAnonymousAccount() {
      // Web SDK 超時（15秒）
      const webTimeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('建立匿名帳號超時，請檢查網路連線')), 15000)
      })

      try {
        if (useNativeAuth) {
          // iOS: 使用 Capacitor 原生 API
          const result = await FirebaseAuthentication.signInAnonymously()

          if (result.user) {
            this.uid = result.user.uid
            this.isAnonymous = result.user.isAnonymous || true
            this.ready = true
            this.startProfileListener()
            return
          } else {
            throw new Error('result.user 為空')
          }
        } else {
          // Android/Web: 使用 Web SDK
          const result = await Promise.race([
            signInAnonymously(auth),
            webTimeoutPromise
          ])
          this.uid = result.user.uid
          this.isAnonymous = true
          this.ready = true
          this.startProfileListener()
        }
      } catch (error: any) {
        console.error('建立匿名帳號失敗:', error)
        throw error
      }
    },

    async createEmailAccount(email: string, password: string) {
      try {
        if (useNativeAuth) {
          // iOS: 使用原生 API 建立帳號
          const result = await FirebaseAuthentication.createUserWithEmailAndPassword({
            email,
            password
          })

          if (result.user) {
            this.uid = result.user.uid
            this.isAnonymous = false
            this.ready = true
            this.startProfileListener()
            return result.user
          } else {
            throw new Error('建立成功但沒有用戶資訊')
          }
        } else {
          // Android/Web: 使用 Web SDK
          const result = await createUserWithEmailAndPassword(auth, email, password)
          this.uid = result.user.uid
          this.isAnonymous = false
          this.ready = true
          this.startProfileListener()
          return result.user
        }
      } catch (error: any) {
        console.error('建立 Email 帳號失敗:', error)

        // 處理常見錯誤
        if (error.code === 'auth/email-already-in-use') {
          throw new Error('此信箱已被使用')
        } else if (error.code === 'auth/invalid-email') {
          throw new Error('信箱格式不正確')
        } else if (error.code === 'auth/weak-password') {
          throw new Error('密碼強度不足（至少 6 碼）')
        } else if (error.code === 'auth/operation-not-allowed') {
          throw new Error('請先至 Firebase Console 啟用 Email/Password 登入方式')
        }

        throw new Error(error.message || '建立帳號失敗，請稍後再試')
      }
    },

    async linkWithEmail(email: string, password: string) {
      try {
        if (useNativeAuth) {
          // iOS: 使用原生 API
          let currentUserResult = await FirebaseAuthentication.getCurrentUser()

          // 如果沒有當前用戶，先建立匿名帳號
          if (!currentUserResult.user) {
            console.log('沒有當前用戶，先建立匿名帳號')
            const anonymousResult = await FirebaseAuthentication.signInAnonymously()
            if (!anonymousResult.user) {
              throw new Error('無法建立匿名帳號')
            }
            this.uid = anonymousResult.user.uid
            this.isAnonymous = true
            this.ready = true
            currentUserResult = anonymousResult
          }

          // 再次確認用戶存在
          if (!currentUserResult.user) {
            throw new Error('無法取得用戶資訊')
          }

          if (!currentUserResult.user.isAnonymous) {
            throw new Error('已經是正式帳號')
          }

          // 使用原生 API 連結 Email 憑證
          await FirebaseAuthentication.linkWithEmailAndPassword({
            email,
            password
          })

          // 更新狀態
          this.isAnonymous = false

          return currentUserResult.user
        } else {
          // Android/Web: 使用 Web SDK
          let user = auth.currentUser

          // 如果沒有當前用戶，先建立匿名帳號
          if (!user) {
            console.log('沒有當前用戶，先建立匿名帳號')
            const anonymousResult = await signInAnonymously(auth)
            this.uid = anonymousResult.user.uid
            this.isAnonymous = true
            this.ready = true
            user = anonymousResult.user
          }

          if (!user.isAnonymous) {
            throw new Error('已經是正式帳號')
          }

          // 創建 Email 憑證
          const credential = EmailAuthProvider.credential(email, password)

          // 將匿名帳號升級為 Email 帳號
          const result = await linkWithCredential(user, credential)

          // 更新狀態
          this.isAnonymous = false

          return result.user
        }
      } catch (error: any) {
        console.error('帳號升級失敗:', error)

        // 處理常見錯誤
        if (error.code === 'auth/email-already-in-use') {
          throw new Error('此信箱已被使用')
        } else if (error.code === 'auth/invalid-email') {
          throw new Error('信箱格式不正確')
        } else if (error.code === 'auth/weak-password') {
          throw new Error('密碼強度不足（至少 8 碼）')
        } else if (error.code === 'auth/operation-not-allowed') {
          throw new Error('請先至 Firebase Console 啟用 Email/Password 登入方式')
        }

        throw new Error(error.message || '建立帳號失敗，請稍後再試')
      }
    },

    async signInWithEmail(email: string, password: string) {
      try {
        if (useNativeAuth) {
          // iOS: 使用原生 API 登入
          const result = await FirebaseAuthentication.signInWithEmailAndPassword({
            email,
            password
          })

          if (result.user) {
            this.uid = result.user.uid
            this.isAnonymous = false
            this.ready = true
            this.startProfileListener()
            return result.user
          } else {
            throw new Error('登入失敗')
          }
        } else {
          // Android/Web: 使用 Web SDK
          const result = await signInWithEmailAndPassword(auth, email, password)

          this.uid = result.user.uid
          this.isAnonymous = false
          this.ready = true
          this.startProfileListener()

          return result.user
        }
      } catch (error: any) {
        console.error('Email 登入失敗:', error)

        // 處理常見錯誤
        if (error.code === 'auth/user-not-found') {
          throw new Error('此帳號不存在')
        } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
          throw new Error('密碼錯誤')
        } else if (error.code === 'auth/invalid-email') {
          throw new Error('信箱格式不正確')
        } else if (error.code === 'auth/too-many-requests') {
          throw new Error('登入嘗試次數過多，請稍後再試')
        } else if (error.code === 'auth/user-disabled') {
          throw new Error('此帳號已被停用')
        }

        throw new Error(error.message || '登入失敗，請稍後再試')
      }
    },

    async deleteAccount(password?: string) {
      // 停止 Firestore 即時監聽，避免帳號刪除後觸發 permission error
      if (profileUnsubscribe) {
        profileUnsubscribe()
        profileUnsubscribe = null
      }

      try {
        if (useNativeAuth) {
          // iOS: 使用原生 API
          const currentUserResult = await FirebaseAuthentication.getCurrentUser()

          if (!currentUserResult.user) {
            throw new Error('未登入')
          }

          if (currentUserResult.user.isAnonymous) {
            // 匿名帳號：直接刪除
            await FirebaseAuthentication.deleteUser()
          } else {
            // Email 帳號：需要重新驗證後刪除
            if (!password) {
              throw new Error('需要輸入密碼以驗證身份')
            }

            const email = currentUserResult.user.email
            if (!email) {
              throw new Error('無法取得信箱資訊')
            }

            // 先重新登入以重新驗證
            await FirebaseAuthentication.signInWithEmailAndPassword({
              email,
              password
            })

            // 重新驗證成功後，刪除帳號
            await FirebaseAuthentication.deleteUser()
          }
        } else {
          // Android/Web: 使用 Web SDK
          const user = auth.currentUser
          if (!user) {
            throw new Error('未登入')
          }

          if (user.isAnonymous) {
            // 匿名帳號：直接刪除
            await deleteUser(user)
          } else {
            // Email 帳號：需要重新驗證後才能刪除
            if (!password) {
              throw new Error('需要輸入密碼以驗證身份')
            }

            // 重新驗證
            const email = user.email
            if (!email) {
              throw new Error('無法取得信箱資訊')
            }

            const credential = EmailAuthProvider.credential(email, password)
            await reauthenticateWithCredential(user, credential)

            // 刪除帳號
            await deleteUser(user)
          }
        }

        // 重置狀態
        this.uid = null
        this.ready = false
        this.isAnonymous = true
        this.hasShownBackupReminder = false
        this.familyCode = null
        this.familyName = null
        this.ownerFamilyName = null
        this.sharedWith = {}
        this.pendingMembers = {}
        this.linkedTo = null
        this.pendingWith = null

        // 不重新初始化，讓頁面重新載入時自然執行 init()
      } catch (error: any) {
        console.error('刪除帳號失敗:', error)

        // 如果用戶已不存在，視為成功
        if (error.code === 'auth/user-not-found') {
          this.uid = null
          this.ready = false
          this.isAnonymous = true
          this.hasShownBackupReminder = false
          this.familyCode = null
          this.familyName = null
          this.ownerFamilyName = null
          this.sharedWith = {}
          this.pendingMembers = {}
          this.linkedTo = null
          this.pendingWith = null
          return // 成功，不拋出錯誤
        }

        // 處理其他常見錯誤
        if (error.code === 'auth/requires-recent-login') {
          throw new Error('為了安全性，請重新登入後再試')
        } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
          throw new Error('密碼錯誤')
        } else if (error.code === 'auth/too-many-requests') {
          throw new Error('操作次數過多，請稍後再試')
        }

        throw new Error(error.message || '刪除帳號失敗，請稍後再試')
      }
    },

    async forceSignOut() {
      // 停止 Firestore 即時監聽
      if (profileUnsubscribe) {
        profileUnsubscribe()
        profileUnsubscribe = null
      }

      try {
        if (useNativeAuth) {
          // iOS: 使用原生 API 登出
          await FirebaseAuthentication.signOut()
        } else {
          // Android/Web: 使用 Web SDK 登出
          await signOut(auth)
        }

        // 重置狀態
        this.uid = null
        this.ready = true
        this.isAnonymous = true
        this.hasShownBackupReminder = false
        this.familyCode = null
        this.familyName = null
        this.ownerFamilyName = null
        this.sharedWith = {}
        this.pendingMembers = {}
        this.linkedTo = null
        this.pendingWith = null
      } catch (error: any) {
        console.error('登出失敗:', error)
        // 即使登出失敗，也強制重置狀態
        this.uid = null
        this.ready = true
        this.isAnonymous = true
        this.hasShownBackupReminder = false
        this.familyCode = null
        this.familyName = null
        this.ownerFamilyName = null
        this.sharedWith = {}
        this.pendingMembers = {}
        this.linkedTo = null
        this.pendingWith = null
      }
    },

    /**
     * 啟動 Firestore 即時監聽 users/{uid}（取代一次性的 loadUserProfile）
     * onSnapshot 首次觸發即完成初始載入，之後資料變更自動更新
     */
    startProfileListener() {
      if (!this.uid) return

      // 清理舊的監聽器
      if (profileUnsubscribe) {
        profileUnsubscribe()
        profileUnsubscribe = null
      }

      // 快取上一次的 ownerUid，避免每次 snapshot 都重複讀取（Issue #5）
      let lastOwnerUid: string | null = null
      // 追蹤前一次狀態，用於偵測審核通過
      let prevLinkedToUid: string | null = this.linkedTo?.uid ?? null
      let prevPendingWithUid: string | null = this.pendingWith?.uid ?? null

      const userRef = doc(db, 'users', this.uid)
      profileUnsubscribe = onSnapshot(userRef, async (snapshot) => {
        if (!snapshot.exists()) return
        const data = snapshot.data()

        this.familyCode = data.familyCode ?? null
        this.familyName = data.familyName ?? null
        this.sharedWith = (data.sharedWith ?? {}) as Record<string, FamilyMember>
        this.pendingMembers = (data.pendingMembers ?? {}) as Record<string, PendingMember>
        this.linkedTo = data.linkedTo ?? null
        this.pendingWith = data.pendingWith ?? null

        // 偵測審核通過：pendingWith 由有變無，且 linkedTo 新設定 → 重載家庭資料
        const newLinkedToUid: string | null = data.linkedTo?.uid ?? null
        const newPendingWithUid: string | null = data.pendingWith?.uid ?? null
        if (prevPendingWithUid && !newPendingWithUid && !prevLinkedToUid && newLinkedToUid) {
          void triggerFamilyUpdate().catch(() => {})
        }
        prevLinkedToUid = newLinkedToUid
        prevPendingWithUid = newPendingWithUid

        // Guest：只在 ownerUid 真的改變時才重新讀取 owner 家庭名稱
        const ownerUid = data.linkedTo?.uid ?? data.pendingWith?.uid ?? null
        if (ownerUid !== lastOwnerUid) {
          lastOwnerUid = ownerUid
          if (ownerUid) {
            const fetchingUid = ownerUid
            try {
              const ownerProfile = await getUserProfile(fetchingUid)
              // 確認 await 期間 ownerUid 沒有被更新的 snapshot 改變
              if (fetchingUid === lastOwnerUid) {
                this.ownerFamilyName = ownerProfile.familyName ?? null
              }
            } catch {
              if (fetchingUid === lastOwnerUid) {
                this.ownerFamilyName = null
              }
            }
          } else {
            this.ownerFamilyName = null
          }
        }
      }, (error) => {
        console.warn('Profile 即時監聽失敗:', error)
      })
    },

    /**
     * 載入使用者家庭 profile（一次性讀取，供 initFromCloud 等呼叫）
     */
    async loadUserProfile() {
      if (!this.uid) return
      try {
        const profile = await getUserProfile(this.uid)
        this.familyCode = profile.familyCode ?? null
        this.familyName = profile.familyName ?? null
        this.sharedWith = profile.sharedWith ?? {}
        this.pendingMembers = (profile.pendingMembers as Record<string, PendingMember>) ?? {}
        this.linkedTo = profile.linkedTo ?? null
        this.pendingWith = profile.pendingWith ?? null

        // Guest：讀取 owner 的家庭名稱（linkedTo 優先，其次 pendingWith）
        const ownerUid = profile.linkedTo?.uid ?? profile.pendingWith?.uid
        if (ownerUid) {
          try {
            const ownerProfile = await getUserProfile(ownerUid)
            this.ownerFamilyName = ownerProfile.familyName ?? null
          } catch {
            this.ownerFamilyName = null
          }
        } else {
          this.ownerFamilyName = null
        }
      } catch (error) {
        console.warn('載入家庭 profile 失敗:', error)
      }
    },
  },
})
